import { useMemo, useState } from 'react'
import { useLanguage } from '@/i18n/LanguageContext'
import { cn } from '@/lib/utils'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import {
  Users, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, Minus,
  BarChart2, ExternalLink, ChevronDown, ChevronUp, CalendarPlus,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import { useFollowerGrowth, type PageFollowerGrowth, type FinancePage } from '@/api/hooks/usePages'
import { PublishStuckDialog } from './PublishStuckDialog'

const fmtNum = new Intl.NumberFormat('pt-BR')

function formatCompact(n: number): string {
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return fmtNum.format(n)
}

// Travada = cresceu menos de 0,5% em 7 dias (sem cair); caindo = delta 7d negativo
const STUCK_PCT = 0.5

type GrowthStatus = 'growing' | 'stuck' | 'falling' | 'unknown'

function statusOf(p: PageFollowerGrowth): GrowthStatus {
  if (p.delta7d === null || p.pct7d === null) return 'unknown'
  if (p.delta7d < 0) return 'falling'
  if (p.pct7d < STUCK_PCT) return 'stuck'
  return 'growing'
}

type SortKey = 'followers' | 'delta24h' | 'delta7d' | 'delta30d' | 'pct7d' | 'pendingPosts'
type StatusFilter = 'all' | GrowthStatus

function DeltaCell({ value }: { value: number | null }) {
  if (value === null) return <span className="text-muted-foreground/50">—</span>
  const cls = value > 0 ? 'text-emerald-500' : value < 0 ? 'text-red-500' : 'text-muted-foreground'
  return <span className={cn('tabular-nums font-medium', cls)}>{value > 0 ? '+' : ''}{fmtNum.format(value)}</span>
}

function Spark({ points, status }: { points: number[]; status: GrowthStatus }) {
  if (points.length < 2) return <span className="text-[10px] text-muted-foreground/50">—</span>
  const min = Math.min(...points)
  const max = Math.max(...points)
  const range = max - min || 1
  const color = status === 'growing' ? 'bg-emerald-500' : status === 'falling' ? 'bg-red-500' : 'bg-muted-foreground/60'
  return (
    <div className="flex items-end gap-px h-6 w-24">
      {points.map((v, i) => (
        <div
          key={i}
          className={cn('flex-1 rounded-sm', i === points.length - 1 ? color : 'bg-muted-foreground/20')}
          style={{ height: `${Math.max(2, ((v - min) / range) * 24)}px` }}
        />
      ))}
    </div>
  )
}

const STATUS_LABEL: Record<GrowthStatus, string> = {
  growing: 'Crescendo',
  stuck: 'Travada',
  falling: 'Caindo',
  unknown: 'Sem dados',
}

const STATUS_CLASS: Record<GrowthStatus, string> = {
  growing: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-500',
  stuck: 'bg-amber-500/10 border-amber-500/20 text-amber-500',
  falling: 'bg-red-500/10 border-red-500/20 text-red-500',
  unknown: 'bg-muted/50 border-border text-muted-foreground',
}

interface Props {
  pages: FinancePage[]
  onSelectChart?: (pageId: string) => void
}

export function FollowerOverview({ onSelectChart }: Props) {
  const { t } = useLanguage()
  const { data: growth, isLoading } = useFollowerGrowth()

  const [sortKey, setSortKey] = useState<SortKey>('delta7d')
  const [sortDesc, setSortDesc] = useState(true)
  const [filter, setFilter] = useState<StatusFilter>('all')
  const [publishOpen, setPublishOpen] = useState(false)

  const withStatus = useMemo(
    () => (growth ?? []).map(p => ({ ...p, status: statusOf(p) })),
    [growth],
  )

  const counts = useMemo(() => ({
    all: withStatus.length,
    growing: withStatus.filter(p => p.status === 'growing').length,
    stuck: withStatus.filter(p => p.status === 'stuck').length,
    falling: withStatus.filter(p => p.status === 'falling').length,
  }), [withStatus])

  const totals = useMemo(() => ({
    followers: withStatus.reduce((s, p) => s + p.followers, 0),
    delta7d: withStatus.reduce((s, p) => s + (p.delta7d ?? 0), 0),
  }), [withStatus])

  const rows = useMemo(() => {
    const filtered = filter === 'all' ? withStatus : withStatus.filter(p => p.status === filter)
    const dir = sortDesc ? -1 : 1
    return [...filtered].sort((a, b) => {
      const av = a[sortKey] ?? Number.NEGATIVE_INFINITY
      const bv = b[sortKey] ?? Number.NEGATIVE_INFINITY
      return av === bv ? 0 : av > bv ? dir : -dir
    })
  }, [withStatus, filter, sortKey, sortDesc])

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDesc(d => !d)
    else { setSortKey(key); setSortDesc(true) }
  }

  const SortHead = ({ label, k, className }: { label: string; k: SortKey; className?: string }) => (
    <TableHead
      className={cn('cursor-pointer select-none whitespace-nowrap text-right', className)}
      onClick={() => toggleSort(k)}
    >
      <span className="inline-flex items-center gap-0.5">
        {label}
        {sortKey === k && (sortDesc ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />)}
      </span>
    </TableHead>
  )

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-3">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-20" />)}
        </div>
        <Skeleton className="h-72" />
      </div>
    )
  }

  if (withStatus.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16 text-muted-foreground">
        <Users className="h-10 w-10 opacity-30" />
        <p className="text-sm">{t('Nenhuma pagina com dados de seguidores')}</p>
        <p className="text-xs text-muted-foreground/60">{t('Importe paginas e aguarde o proximo snapshot (a cada 2h)')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {/* Summary Cards */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card className="p-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Users className="h-5 w-5 text-primary" />
          </div>
          <div>
            <div className="text-2xl font-bold">{formatCompact(totals.followers)}</div>
            <div className="text-xs text-muted-foreground">{t('Total Seguidores')}</div>
          </div>
        </Card>
        <Card className="p-4 flex items-center gap-3">
          <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${totals.delta7d >= 0 ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
            {totals.delta7d >= 0
              ? <TrendingUp className="h-5 w-5 text-emerald-500" />
              : <TrendingDown className="h-5 w-5 text-red-500" />
            }
          </div>
          <div>
            <div className={`text-2xl font-bold ${totals.delta7d > 0 ? 'text-emerald-500' : totals.delta7d < 0 ? 'text-red-500' : ''}`}>
              {totals.delta7d > 0 ? '+' : ''}{fmtNum.format(totals.delta7d)}
            </div>
            <div className="text-xs text-muted-foreground">{t('Variacao 7 dias')}</div>
          </div>
        </Card>
        <Card className="p-4 flex items-center gap-3">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <ArrowUpRight className="h-4 w-4 text-emerald-500" />
              <span className="text-lg font-bold text-emerald-500">{counts.growing}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Minus className="h-4 w-4 text-amber-500" />
              <span className="text-lg font-bold text-amber-500">{counts.stuck}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <ArrowDownRight className="h-4 w-4 text-red-500" />
              <span className="text-lg font-bold text-red-500">{counts.falling}</span>
            </div>
          </div>
          <div className="text-xs text-muted-foreground ml-2">{t('Crescendo / Travadas / Caindo')}</div>
        </Card>
      </div>

      {/* Status filter + acao */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-1 rounded-lg bg-muted p-0.5 w-fit">
          {([
            ['all', `${t('Todas')} (${counts.all})`],
            ['growing', `${t('Crescendo')} (${counts.growing})`],
            ['stuck', `${t('Travadas')} (${counts.stuck})`],
            ['falling', `${t('Caindo')} (${counts.falling})`],
          ] as [StatusFilter, string][]).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              className={cn(
                'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                filter === key ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {label}
            </button>
          ))}
        </div>
        <Button
          size="sm"
          className="gap-1.5"
          disabled={counts.stuck + counts.falling === 0}
          onClick={() => setPublishOpen(true)}
        >
          <CalendarPlus className="h-4 w-4" /> {t('Publicar nas travadas')}
        </Button>
      </div>

      {/* Ranking table */}
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('Pagina')}</TableHead>
              <SortHead label={t('Seguidores')} k="followers" />
              <SortHead label="24h" k="delta24h" />
              <SortHead label="7d" k="delta7d" />
              <SortHead label="% 7d" k="pct7d" />
              <SortHead label="30d" k="delta30d" />
              <TableHead className="text-center">30d</TableHead>
              <SortHead label={t('Fila')} k="pendingPosts" />
              <TableHead>{t('Status')}</TableHead>
              <TableHead className="w-[80px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(p => {
              const fbUrl = p.link || `https://www.facebook.com/${p.fbPageId}`
              return (
                <TableRow key={p.pageId}>
                  <TableCell className="max-w-[220px]">
                    <a
                      href={fbUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 group"
                      title={t('Abrir pagina no Facebook')}
                    >
                      <img
                        src={`/api/v2/page-picture/${p.fbPageId}`}
                        alt=""
                        className="h-7 w-7 shrink-0 rounded-full bg-muted object-cover"
                        onError={(e) => { (e.target as HTMLImageElement).style.visibility = 'hidden' }}
                      />
                      <span className="truncate text-sm font-medium group-hover:text-primary group-hover:underline">
                        {p.pageName}
                      </span>
                    </a>
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-semibold">{formatCompact(p.followers)}</TableCell>
                  <TableCell className="text-right text-xs"><DeltaCell value={p.delta24h} /></TableCell>
                  <TableCell className="text-right text-xs"><DeltaCell value={p.delta7d} /></TableCell>
                  <TableCell className="text-right text-xs tabular-nums text-muted-foreground">
                    {p.pct7d === null ? '—' : `${p.pct7d > 0 ? '+' : ''}${p.pct7d.toFixed(2).replace('.', ',')}%`}
                  </TableCell>
                  <TableCell className="text-right text-xs"><DeltaCell value={p.delta30d} /></TableCell>
                  <TableCell><Spark points={p.spark} status={p.status} /></TableCell>
                  <TableCell className="text-right">
                    {p.pendingPosts > 0 ? (
                      <div className="text-xs tabular-nums">
                        <span className="font-medium">{p.pendingPosts}</span>
                        {p.lastPublishedAt && (
                          <div className="text-[9px] text-muted-foreground">
                            {t('ult.')} {Math.floor((Date.now() - p.lastPublishedAt) / 86400000)}d
                          </div>
                        )}
                      </div>
                    ) : (
                      <Badge variant="outline" className="text-[9px] bg-red-500/10 border-red-500/20 text-red-500 whitespace-nowrap">
                        {t('Sem conteudo')}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={cn('text-[10px] border', STATUS_CLASS[p.status])}>
                      {t(STATUS_LABEL[p.status])}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <a href={fbUrl} target="_blank" rel="noopener noreferrer" title={t('Abrir no Facebook')}>
                        <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-primary">
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Button>
                      </a>
                      {onSelectChart && (
                        <Button
                          variant="ghost" size="icon"
                          className="h-6 w-6 text-muted-foreground hover:text-primary"
                          title={t('Grafico')}
                          onClick={() => onSelectChart(p.pageId)}
                        >
                          <BarChart2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </Card>

      <PublishStuckDialog
        open={publishOpen}
        onOpenChange={setPublishOpen}
        pages={withStatus.filter(p => p.status === 'stuck' || p.status === 'falling')}
      />
    </div>
  )
}
